import * as React from "react";
import { useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { qualifyUrl } from "util/URLUtils";
import fetch from "logic/rest/FetchProvider";
import UserNotification from "util/UserNotification";
import { WatchlistActions, ApiRoutes } from "watchlist/stores/WatchlistStore";
import defaultWatchlistFieldTypes from "security-content/mappings/watchlistFields";
import useLicenseCheck from "license/useLicenseCheck";
import { parseIfArrayValue } from "search/messageFieldValueHelper";
import WatchlistStatusContext from "./WatchlistStatusContext";
const parseFieldValue = (messageFieldValue) => {
  const parsedArrayValue = parseIfArrayValue(messageFieldValue);
  return parsedArrayValue != null ? parsedArrayValue : messageFieldValue;
};
const getMatchingFields = (watchlistFields, messageFields) => watchlistFields.reduce((matches, fieldName) => {
  const messageFieldValue = messageFields[fieldName];
  if (messageFieldValue !== void 0) {
    return {
      ...matches,
      [fieldName]: parseFieldValue(messageFieldValue)
    };
  }
  return matches;
}, {});
const getMatchingWatchlistFieldsByType = (watchlistFieldTypes, messageFields) => Object.entries(watchlistFieldTypes).reduce((result, [watchlistFieldType, watchlistFields]) => {
  const matchingFields = getMatchingFields(watchlistFields, messageFields);
  if (Object.values(matchingFields).length !== 0) {
    return {
      ...result,
      [watchlistFieldType]: matchingFields
    };
  }
  return result;
}, {});
const checkWatchlistStatus = (fields) => {
  const url = qualifyUrl(ApiRoutes.WatchlistApiController.checkStatusForFields().url);
  return fetch("POST", url, fields).catch(
    (error) => UserNotification.error(`Checking watchlist status for message fields failed with status: ${String(error)}`)
  );
};
const filterEmptyLists = (fieldsWatchlistStatus) => {
  if (!fieldsWatchlistStatus) {
    return void 0;
  }
  return Object.entries(fieldsWatchlistStatus).reduce((lists, [watchlistFieldType, watchlistFields]) => {
    if (!watchlistFields || watchlistFields.length === 0) {
      return lists;
    }
    return { ...lists, [watchlistFieldType]: watchlistFields };
  }, {});
};
const useRefetchOnStatusChange = (refetch, matchingWatchlistFieldsByType) => {
  const refetchOnDemand = useCallback(
    ({ payload: { watchlistFieldType, value: updatedValue } }) => {
      const shouldUpdate = Object.values(matchingWatchlistFieldsByType[watchlistFieldType]).find(
        (fieldValue) => fieldValue === updatedValue
      ) !== void 0;
      if (shouldUpdate) {
        refetch();
      }
    },
    [matchingWatchlistFieldsByType, refetch]
  );
  useEffect(() => {
    const unlistenUpdate = WatchlistActions.update.completed.listen(refetchOnDemand);
    const unlistenDelete = WatchlistActions.delete.completed.listen(refetchOnDemand);
    return () => {
      unlistenUpdate();
      unlistenDelete();
    };
  }, [refetch, refetchOnDemand]);
};
const WatchlistStatusProvider = ({ children, message, watchlistFieldTypes = defaultWatchlistFieldTypes }) => {
  const matchingWatchlistFieldsByType = getMatchingWatchlistFieldsByType(watchlistFieldTypes, message.fields);
  const shouldCheckStatus = Object.keys(matchingWatchlistFieldsByType).length !== 0;
  const {
    security: { isValid: isValidSecurityLicense }
  } = useLicenseCheck();
  const {
    data: fieldsWatchlistStatus,
    isPending,
    refetch
  } = useQuery({
    queryKey: ["watchlistStatus", message.id],
    queryFn: () => checkWatchlistStatus(matchingWatchlistFieldsByType),
    enabled: !!(shouldCheckStatus && isValidSecurityLicense)
  });
  useRefetchOnStatusChange(refetch, matchingWatchlistFieldsByType);
  const contextValue = useMemo(
    () => ({
      refetch,
      isLoading: isPending,
      status: filterEmptyLists(fieldsWatchlistStatus)
    }),
    [fieldsWatchlistStatus, isPending, refetch]
  );
  return /* @__PURE__ */ React.createElement(WatchlistStatusContext.Provider, { value: contextValue }, children);
};
export default WatchlistStatusProvider;
